import { Grid } from '@mui/material';
import { ChangeEvent, FC, ReactElement, useContext, useEffect, useState } from 'react';
import { ReactComponent as UpcomingServiceIcon } from '../../../assets/images/clipboard-export-wrapper.svg';
import Button from '@mui/material/Button';
import InputText from '../../../components/form/InputText';
import TemplateBox from '../../form-templates/TemplateBox';
import { UpcomingServiceContext } from '../../../context/upcomingServiceContext';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import InputDateHour from '../../../components/form/InputDateHour';
import { validateForm } from '../../../utils/form';
import { upcomingServiceSchema } from './UpcomingServiceSchema';
import { getExpirationStateFromDate } from '../../../utils/lib';

interface UpdateUpcomingServiceDto {
  service_date: string;
  title: string;
  additional_information: string;
}

const updateUpcomingSeriveFormInitialState: UpdateUpcomingServiceDto = {
  service_date: '',
  title: '',
  additional_information: ''
};

const updateUpcomingFormValidationErrorsInitialState: UpdateUpcomingServiceDto = {
  service_date: '',
  title: '',
  additional_information: ''
};

const UpcomingServiceForm: FC = (): ReactElement => {
  const { updateUpcomingService, state: upcomingServiceState } = useContext(UpcomingServiceContext);
  const [form, setForm] = useState<UpdateUpcomingServiceDto>(updateUpcomingSeriveFormInitialState);
  const [formValidationErrors, setFormValidationErrors] = useState<UpdateUpcomingServiceDto>(
    updateUpcomingFormValidationErrorsInitialState
  );
  const location = useLocation();
  const { upcomingService } = location.state;

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    setForm({
      ...form,
      service_date: value ? value.toISOString() : ''
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitForm = () => {
    const { errorSummary } = validateForm(upcomingServiceSchema, form);

    setFormValidationErrors(errorSummary);

    if (!Object.values(errorSummary).length) {
      updateUpcomingService(upcomingService.id, {
        service_date: form.service_date,
        title: form.title,
        additional_information: form.additional_information,
        vessel_id: upcomingService.vessel_id,
        state: getExpirationStateFromDate(form.service_date)
      });
    }
  };

  useEffect(() => {
    if (upcomingService) {
      setForm({
        service_date: upcomingService.service_date,
        title: upcomingService.title,
        additional_information: upcomingService.additional_information
      });
    } else {
      setForm(updateUpcomingSeriveFormInitialState);
    }
  }, [upcomingService]);

  return (
    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
      <Grid
        container
        display={'flex'}
        justifyContent={'space-between'}
        style={{ padding: '46px 0 27px 0', alignItems: 'center' }}>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          style={{ display: 'flex', alignItems: 'center' }}>
          <UpcomingServiceIcon className="icon-title" />
          <p style={{ color: '#273F82', fontWeight: 'bold' }}>Edit Upcoming Service</p>
        </Grid>

        <Grid item xs={12} sm={12} md={1} lg={1} xl={1.5} />
        <Grid item xs={12} sm={12} md={1} lg={1} xl={1.5} />
        <Grid item xs={12} sm={12} md={1} lg={1.5} xl={1.5}>
          <Button
            disabled={upcomingServiceState.update.loading}
            variant="outlined"
            type="button"
            onClick={handleSubmitForm}
            style={{ width: '100%', marginBottom: '20px' }}>
            Update
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <TemplateBox minHeight={'calc(100vh - 240px)'}>
          <InputText
            name={'title'}
            label={'Title'}
            value={form.title}
            validationErrors={formValidationErrors.title}
            onChange={handleInputChange}
            width={'100%'}
          />
          <InputDateHour
            name={'service_date'}
            label="due date"
            calendarProps={{
              value: form.service_date,
              onChange: handleDateChange
            }}
            validationError={formValidationErrors.service_date || undefined}
          />
          <InputText
            name={'additional_information'}
            label={'Additional Information'}
            value={form.additional_information}
            validationErrors={formValidationErrors.additional_information}
            onChange={handleInputChange}
            width={'100%'}
          />
        </TemplateBox>
      </Grid>
    </Grid>
  );
};

export default UpcomingServiceForm;
